import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { HardHat } from 'lucide-react';
import { toast } from 'sonner';

import { usePmProperty } from '@/hooks/usePmProperty';
import { useTranslation } from '@/hooks/useTranslation';
import { closeCrewSession, listCrewSessions, type CrewSession } from '@/services/api.client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const STATUS_STYLES: Record<CrewSession['status'], string> = {
  open: 'bg-[var(--surface-2)] text-[var(--text-secondary)]',
  reconciled: 'bg-[var(--success-bg,#dcfce7)] text-[var(--success-fg,#166534)]',
  unreconciled: 'bg-[var(--danger-bg,#fee2e2)] text-[var(--danger-fg,#991b1b)]',
  closed_manual: 'bg-[var(--surface-2)] text-[var(--text-secondary)]',
};

export function CrewSessionsPage() {
  const { t } = useTranslation();
  const pm = usePmProperty();
  const propertyId = pm?.propertyId ?? '';
  const queryClient = useQueryClient();

  const [showUnreconciledOnly, setShowUnreconciledOnly] = useState(false);
  const [closing, setClosing] = useState<CrewSession | null>(null);
  const [reason, setReason] = useState('');

  const sessionsQuery = useQuery({
    queryKey: ['pm', 'crew-sessions', propertyId, showUnreconciledOnly],
    queryFn: () =>
      listCrewSessions(propertyId, showUnreconciledOnly ? { status: 'unreconciled' } : undefined),
    enabled: !!propertyId,
  });

  const closeMutation = useMutation({
    mutationFn: (vars: { id: string; reason: string }) =>
      closeCrewSession(propertyId, vars.id, vars.reason),
    onSuccess: () => {
      toast.success(t('crew.close.success'));
      setClosing(null);
      setReason('');
      void queryClient.invalidateQueries({ queryKey: ['pm', 'crew-sessions', propertyId] });
    },
    onError: () => toast.error(t('crew.close.error')),
  });

  const sessions = sessionsQuery.data ?? [];

  return (
    <div className="space-y-6 p-4 md:p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--text-primary)]">{t('crew.title')}</h1>
          <p className="text-sm text-[var(--text-secondary)]">{t('crew.subtitle')}</p>
        </div>
        <Button
          variant={showUnreconciledOnly ? 'default' : 'outline'}
          size="sm"
          onClick={() => setShowUnreconciledOnly((v) => !v)}
        >
          {t('crew.filter.unreconciledOnly')}
        </Button>
      </header>

      {sessionsQuery.isLoading && (
        <div className="space-y-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg bg-[var(--surface-2)]" />
          ))}
        </div>
      )}

      {sessionsQuery.isError && (
        <div
          role="alert"
          className="rounded-lg border border-[var(--border)] bg-[var(--surface-1)] p-6 text-center"
        >
          <p className="text-sm text-[var(--text-secondary)]">{t('crew.error')}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3"
            onClick={() => sessionsQuery.refetch()}
          >
            {t('common.retry')}
          </Button>
        </div>
      )}

      {!sessionsQuery.isLoading && !sessionsQuery.isError && sessions.length === 0 && (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-[var(--border)] p-10 text-center">
          <HardHat className="size-8 text-[var(--text-secondary)]" aria-hidden="true" />
          <p className="text-sm text-[var(--text-secondary)]">{t('crew.empty')}</p>
        </div>
      )}

      {sessions.length > 0 && (
        <ul className="space-y-3">
          {sessions.map((s) => (
            <li
              key={s.id}
              className="rounded-lg border border-[var(--border)] bg-[var(--surface-1)] p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-[var(--text-primary)]">{s.contractorName}</p>
                  <p className="text-xs text-[var(--text-secondary)]">
                    {t('crew.unit')}: {s.unitReference} · {s.sessionDate}
                  </p>
                </div>
                <span
                  className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_STYLES[s.status]}`}
                >
                  {t(`crew.status.${s.status}`)}
                </span>
              </div>

              <div className="mt-3 flex flex-wrap gap-4 text-sm">
                <span className="text-[var(--text-secondary)]">
                  {t('crew.expected')}:{' '}
                  <strong className="text-[var(--text-primary)]">{s.expectedCrew}</strong>
                </span>
                <span className="text-[var(--text-secondary)]">
                  {t('crew.in')}:{' '}
                  <strong className="text-[var(--text-primary)]">{s.totalIn}</strong>
                </span>
                <span className="text-[var(--text-secondary)]">
                  {t('crew.out')}:{' '}
                  <strong className="text-[var(--text-primary)]">{s.totalOut}</strong>
                </span>
                <span className="text-[var(--text-secondary)]">
                  {t('crew.inside')}:{' '}
                  <strong
                    className={
                      s.insideCount > 0
                        ? 'text-[var(--danger-fg,#991b1b)]'
                        : 'text-[var(--text-primary)]'
                    }
                  >
                    {s.insideCount}
                  </strong>
                </span>
              </div>

              {s.status === 'unreconciled' && (
                <div className="mt-3">
                  <Button size="sm" variant="outline" onClick={() => setClosing(s)}>
                    {t('crew.close.action')}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <Dialog open={!!closing} onOpenChange={(open) => !open && setClosing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('crew.close.title')}</DialogTitle>
            <DialogDescription>{t('crew.close.description')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="crew-close-reason">{t('crew.close.reasonLabel')}</Label>
            <Textarea
              id="crew-close-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('crew.close.reasonPlaceholder')}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClosing(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              disabled={reason.trim().length === 0 || closeMutation.isPending}
              onClick={() =>
                closing && closeMutation.mutate({ id: closing.id, reason: reason.trim() })
              }
            >
              {t('crew.close.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
